import React, { useContext, useEffect, useState } from 'react';
import '../../assets/css/viewtrree.css';
import Stage3DataPanel from './Stage3DataPanel';
import { DataContext } from './DataContext';
import { ErrorBoundary } from '../common/ErrorBoundary';
import Loader from '../common/Loader';
import MenuContext from './MenuContext';
import Stage3FeatureLevelForecasting from './Stage3FeatureLevelForecasting';
import { useFormContext } from './FormContext';
import { BarChart3, Package, ArrowRight, CheckCircle2, RefreshCw, Layers, MapPin } from "lucide-react";
import { toTitleCase } from "../feturebasedforecasting/Stage3FeatureLevelForecasting/utils/normalizers";

import { stageLayoutStyles, stageFormStyles, StageNavButtons } from "./helper";

const LABEL = {
    feature: 'Feature Based Forecasting',
    sku: 'SKU Based Forecasting',
};

export default function Stage3() {
    const wrapperStyle = stageLayoutStyles.wrapper;
    const cardStyle = stageLayoutStyles.card;

    const { products, geography, isloading } = useContext(DataContext);
    const { formData, updateFormData } = useFormContext();
    const { setActiveTab } = useContext(MenuContext);

    // Local flag: when true, always show the selection screen regardless of persisted type.
    // This lets the Back button from a flow return to the selection screen without losing the saved selection.
    const [showSelectionScreen, setShowSelectionScreen] = useState(false);

    // forecastingType is persisted in formData → survives page refresh and step navigation
    const forecastingType = formData?.stage3?.forecastingType || null;
    const featureSelection = formData?.stage3?.featureBasedForecasting;
    const hasSelectedGroups = Boolean(featureSelection?.selected_combinations?.length);
    const hasFinalizedFeatureSelection = Boolean(
        hasSelectedGroups && formData?.stage3?.products?.length
    );

    // Feature flow has two views: the group builder (combinations table) and the
    // Define Dimensions summary shown after Next. Land on the summary only when
    // groups were already selected and finalized (e.g. returning from Stage 4).
    const [featureView, setFeatureView] = useState(() =>
        hasFinalizedFeatureSelection ? 'summary' : 'builder'
    );

    // Selected Groups table (Define Dimensions summary) paginates 50 at a time
    const [groupsPage, setGroupsPage] = useState(1);
    const GROUPS_PER_PAGE = 50;
    useEffect(() => {
        setGroupsPage(1);
    }, [featureSelection?.selected_combinations?.length]);

    // Commit a forecasting type choice: persists it and exits the selection screen
    const handleSelectType = (type) => {
        updateFormData('stage3', { forecastingType: type });
        setShowSelectionScreen(false);
        // Re-entering the feature flow from the cards always starts at the
        // group builder so the user can adjust their selection.
        if (type === 'feature') setFeatureView('builder');
    };

    // Called by the Back button inside feature / SKU flows
    const handleBackFromFlow = () => setShowSelectionScreen(true);

    // ── Geography: only India exists, so auto-select it in both modes. ──────
    // The stored entry keeps the full hierarchy breadcrumb (India\India\...\India)
    // because every downstream API payload depends on that format — only the
    // UI ever shortens it to "India".
    const geoLeaves = (() => {
        const geoData = geography?.data;
        if (!geoData || typeof geoData !== 'object') return [];
        const levelKeys = Object.keys(geoData)
            .filter((key) => /^level\d+$/.test(key))
            .sort((a, b) => Number(a.replace('level', '')) - Number(b.replace('level', '')));
        const deepestKey = levelKeys[levelKeys.length - 1];
        return Array.isArray(geoData?.[deepestKey]) ? geoData[deepestKey] : [];
    })();

    useEffect(() => {
        if (formData?.stage3?.geography?.length) return;
        if (!geoLeaves.length) return;
        updateFormData('stage3', {
            geography: geoLeaves.map((leaf) => ({ id: leaf.id, breadcrumb: leaf.breadcrumb })),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geoLeaves.length, formData?.stage3?.geography?.length]);

    // Derived view flags
    const showFeature = forecastingType === 'feature' && !showSelectionScreen;
    const showSku = forecastingType === 'sku' && !showSelectionScreen;

    // ── Loading ────────────────────────────────────────────────────────────
    if (isloading) {
        return (
            <ErrorBoundary>
                <div className="loader-wrapper">
                    <Loader size="large" />
                </div>
            </ErrorBoundary>
        );
    }

    // ── Feature-level flow ────────────────────────────────────────────────
    // Stays in 'feature' mode throughout: after group selection the user gets a
    // feature-specific Define Dimensions summary (no SKU sections), never the
    // SKU flow. forecastingType is NOT overwritten.
    if (showFeature) {
        // No groups selected yet → always show the builder, never an empty summary
        if (featureView === 'builder' || !hasSelectedGroups) {
            return (
                <Stage3FeatureLevelForecasting
                    onBack={handleBackFromFlow}
                    onNext={() => setFeatureView('summary')}
                />
            );
        }

        const selectedGroups = [...(featureSelection?.selected_combinations || [])].sort(
            (a, b) =>
                Number(String(a.groupLabel || '').replace(/\D/g, '') || 0) -
                Number(String(b.groupLabel || '').replace(/\D/g, '') || 0)
        );

        const totalGroupsPages = Math.max(1, Math.ceil(selectedGroups.length / GROUPS_PER_PAGE));
        const safeGroupsPage = Math.min(groupsPage, totalGroupsPages);
        const groupsPageStart = (safeGroupsPage - 1) * GROUPS_PER_PAGE;
        const pagedGroups = selectedGroups.slice(groupsPageStart, groupsPageStart + GROUPS_PER_PAGE);

        return (
            <ErrorBoundary>
                <div style={wrapperStyle}>
                    <div style={cardStyle}>
                        <h2 className="text-[16px] text-[#0c3c54] text-left mb-4"
                            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
                            Step 3: Define Dimensions
                        </h2>

                        <SelectionBadge type="feature" onChangeSelection={handleBackFromFlow} />

                        {/* ── Selected groups ── */}
                        <div style={{
                            background: '#ffffff', border: '2px solid #1F4280', borderRadius: 12,
                            overflow: 'hidden', marginBottom: 24,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #2e3a6f 0%, #202b70 100%)',
                                color: '#fff', padding: '14px 18px',
                                display: 'flex', alignItems: 'center', gap: 10,
                            }}>
                                <Layers size={20} />
                                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                                    Selected Groups
                                </h3>
                            </div>

                            {selectedGroups.length === 0 ? (
                                <p style={{ padding: 20, margin: 0, color: '#5E6775', fontFamily: "Poppins, sans-serif", fontSize: 14 }}>
                                    No groups selected yet. Go back to pick feature combinations.
                                </p>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "Poppins, sans-serif" }}>
                                        <thead>
                                            <tr style={{ background: '#032B4E', color: '#fff' }}>
                                                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Group</th>
                                                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Features</th>
                                                <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 14, fontWeight: 600 }}>SKUs</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagedGroups.map((group) => (
                                                <tr key={group.combinationId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                    <td style={{ padding: '12px 16px', fontWeight: 700, color: '#032B4E', fontSize: 14, whiteSpace: 'nowrap' }}>
                                                        {group.groupLabel || group.combinationId}
                                                    </td>
                                                    <td style={{ padding: '12px 16px' }}>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                            {Object.entries(group.combination || {}).map(([key, value]) => (
                                                                <span key={key} style={{
                                                                    background: '#e8f1f8', border: '1px solid #cfe0ec',
                                                                    borderRadius: 20, padding: '3px 10px',
                                                                    fontSize: 12, fontWeight: 600, color: '#032B4E',
                                                                    whiteSpace: 'nowrap',
                                                                }}>
                                                                    {key}: {toTitleCase(value) ?? '-'}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, fontSize: 14, color: '#263243' }}>
                                                        {group.skuCount ?? group.skus?.length ?? '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {selectedGroups.length > 0 && (
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    flexWrap: 'wrap', gap: 10, padding: '12px 16px',
                                    borderTop: '1px solid #e5e7eb', fontFamily: "Poppins, sans-serif",
                                }}>
                                    <span style={{ fontSize: 12.5, color: '#5E6775' }}>
                                        Showing {groupsPageStart + 1}-{Math.min(groupsPageStart + GROUPS_PER_PAGE, selectedGroups.length)} of {selectedGroups.length} groups
                                    </span>

                                    {totalGroupsPages > 1 && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <button
                                                type="button"
                                                onClick={() => setGroupsPage((p) => Math.max(1, p - 1))}
                                                disabled={safeGroupsPage === 1}
                                                style={{
                                                    border: '1.5px solid #1F4280', borderRadius: 6, background: '#fff',
                                                    color: '#032B4E', fontSize: 12.5, fontWeight: 700, padding: '5px 12px',
                                                    cursor: safeGroupsPage === 1 ? 'not-allowed' : 'pointer',
                                                    opacity: safeGroupsPage === 1 ? 0.5 : 1,
                                                }}
                                            >
                                                Previous
                                            </button>
                                            <span style={{ fontSize: 12.5, color: '#374151', fontWeight: 600 }}>
                                                Page {safeGroupsPage} of {totalGroupsPages}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setGroupsPage((p) => Math.min(totalGroupsPages, p + 1))}
                                                disabled={safeGroupsPage === totalGroupsPages}
                                                style={{
                                                    border: '1.5px solid #1F4280', borderRadius: 6, background: '#fff',
                                                    color: '#032B4E', fontSize: 12.5, fontWeight: 700, padding: '5px 12px',
                                                    cursor: safeGroupsPage === totalGroupsPages ? 'not-allowed' : 'pointer',
                                                    opacity: safeGroupsPage === totalGroupsPages ? 0.5 : 1,
                                                }}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* ── Geography (auto-selected India) ── */}
                        <div style={{
                            background: '#ffffff', border: '2px solid #1F4280', borderRadius: 12,
                            overflow: 'hidden', marginBottom: 24,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #2e3a6f 0%, #202b70 100%)',
                                color: '#fff', padding: '14px 18px',
                                display: 'flex', alignItems: 'center', gap: 10,
                            }}>
                                <MapPin size={20} />
                                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                                    Geographic Regions
                                </h3>
                            </div>
                            <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    background: '#e8f1f8', border: '1px solid #cfe0ec',
                                    borderRadius: 20, padding: '5px 14px',
                                    fontFamily: "Poppins, sans-serif", fontSize: 13, fontWeight: 700, color: '#032B4E',
                                }}>
                                    <CheckCircle2 size={14} color="#E06639" />
                                    India
                                </span>
                                <span style={{ fontFamily: "Poppins, sans-serif", fontSize: 12, color: '#5E6775' }}>
                                    Auto-selected — the only available region.
                                </span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 30 }}>
                            <button
                                onClick={() => setFeatureView('builder')}
                                className="px-8 py-2.5 bg-[#E06639] hover:bg-[#c4552a] text-white rounded-lg transition-all shadow-lg hover:shadow-xl"
                                style={{ fontFamily: "Poppins, sans-serif", fontSize: 14, fontWeight: 600 }}
                            >
                                ← Back
                            </button>
                            <button
                                onClick={() => setActiveTab('Stage4')}
                                disabled={!selectedGroups.length}
                                className="px-8 py-2.5 rounded-lg bg-[#032B4E] text-white hover:bg-[#1F4280] shadow-lg hover:shadow-xl flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ fontFamily: "Poppins, sans-serif", fontSize: 14, fontWeight: 600 }}
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        );
    }

    // ── SKU-level flow ────────────────────────────────────────────────────
    if (showSku) {
        return (
            <ErrorBoundary>
                <div style={wrapperStyle}>
                    <div style={cardStyle}>
                        <h2 className="text-[16px] text-[#0c3c54] text-left mb-4"
                            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
                            Step 3: Define Dimensions
                        </h2>

                        <SelectionBadge
                            type="sku"
                            onContinue={() => {/* already in SKU view */ }}
                            onChangeSelection={handleBackFromFlow}
                        />

                        <Stage3DataPanel
                            zone={{ zoneId: 1, zoneName: 'Product Categories' }}
                            hierarchyData={products?.data || []}
                        />
                        <Stage3DataPanel
                            key={formData?.stage3?.geography?.length ? 'geo-autoselected' : 'geo-empty'}
                            zone={{ zoneId: 2, zoneName: 'Geographic Regions', icon: '🌐' }}
                            hierarchyData={geography?.data || []}
                        />

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 30 }}>
                            <button
                                onClick={handleBackFromFlow}
                                className="px-8 py-2.5 bg-[#E06639] hover:bg-[#c4552a] text-white rounded-lg transition-all shadow-lg hover:shadow-xl"
                                style={{ fontFamily: "Poppins, sans-serif", fontSize: 14, fontWeight: 600 }}
                            >
                                ← Back
                            </button>
                            <button
                                onClick={() => setActiveTab('Stage4')}
                                className="px-8 py-2.5 rounded-lg bg-[#032B4E] text-white hover:bg-[#1F4280] shadow-lg hover:shadow-xl flex items-center gap-2 transition-all"
                                style={{ fontFamily: "Poppins, sans-serif", fontSize: 14, fontWeight: 600 }}
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        );
    }

    // ── Selection screen ──────────────────────────────────────────────────
    return (
        <ErrorBoundary>
            <div style={wrapperStyle}>
                <div style={cardStyle}>
                    <h2 className="text-[16px] text-[#0c3c54] text-left mb-4"
                        style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
                        Step 3: Define Dimensions
                    </h2>

                    {/* Persisted-selection banner — shown when returning via Back */}
                    {forecastingType && (
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: 16, flexWrap: 'wrap',
                            background: 'linear-gradient(135deg, #fff8ec, #fffdf6)',
                            border: '1.5px solid #e8c87a',
                            borderLeft: '5px solid #E06639',
                            borderRadius: 10,
                            padding: '14px 20px',
                            marginBottom: 24,
                            boxShadow: '0 4px 12px rgba(184,132,47,0.1)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <CheckCircle2 size={20} color="#E06639" />
                                <span style={{ fontFamily: "Poppins, sans-serif", fontSize: 14, color: '#263243' }}>
                                    Previously selected:&nbsp;
                                    <strong style={{ color: '#032B4E' }}>{LABEL[forecastingType]}</strong>
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button
                                    onClick={() => setShowSelectionScreen(false)}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        background: '#032B4E', color: '#fff',
                                        border: 'none', borderRadius: 8,
                                        padding: '8px 18px',
                                        fontFamily: "Poppins, sans-serif", fontSize: 13, fontWeight: 700,
                                        cursor: 'pointer',
                                    }}
                                >
                                    Continue <ArrowRight size={15} />
                                </button>
                                <button
                                    onClick={() => updateFormData('stage3', { forecastingType: null })}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        background: '#fff', color: '#5E6775',
                                        border: '1.5px solid #d1d5db', borderRadius: 8,
                                        padding: '8px 14px',
                                        fontFamily: "Poppins, sans-serif", fontSize: 13, fontWeight: 600,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <RefreshCw size={14} /> Change
                                </button>
                            </div>
                        </div>
                    )}

                    <style>{`
                        @keyframes fadeInUp {
                            from { opacity: 0; transform: translateY(30px); }
                            to   { opacity: 1; transform: translateY(0); }
                        }
                        @keyframes floatIcon {
                            0%,100% { transform: translateY(0); }
                            50%      { transform: translateY(-8px); }
                        }
                        @keyframes iconPulse {
                            0%   { box-shadow: 0 0 0 0 rgba(3,43,78,0.4); }
                            70%  { box-shadow: 0 0 0 20px rgba(3,43,78,0); }
                            100% { box-shadow: 0 0 0 0 rgba(3,43,78,0); }
                        }
                        .anim-fade-in-1 { animation: fadeInUp 0.7s cubic-bezier(0.16,1,0.3,1) forwards; }
                        .anim-fade-in-2 { animation: fadeInUp 0.7s cubic-bezier(0.16,1,0.3,1) 0.15s forwards; opacity: 0; }
                        .level-card:hover .level-icon        { animation: floatIcon 2s ease-in-out infinite; }
                        .level-card:hover .level-icon-bg     { animation: iconPulse 2s infinite; background-color: #032B4E; }
                        .level-card:hover .level-icon svg    { color: #ffffff; }
                        .level-card--selected                { border-color: #E06639 !important; box-shadow: 0 0 0 3px rgba(184,132,47,0.18); }
                    `}</style>

                    <div className="relative grid grid-cols-1 md:grid-cols-2 gap-12 mt-6 mb-16 px-4">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-[120%] bg-gradient-to-r from-[#eef2f6] to-[#fcfaf5] blur-3xl -z-10 rounded-[100px] opacity-70 pointer-events-none" />

                        {/* SKU Level Card */}
                        <div
                            onClick={() => handleSelectType('sku')}
                            className={`level-card group cursor-pointer relative bg-white rounded-2xl border-2 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden flex flex-col h-full anim-fade-in-2 ${forecastingType === 'sku' ? 'level-card--selected' : 'border-[#1F4280]'}`}
                        >
                            {forecastingType === 'sku' && <SelectedChip />}
                            <div className="absolute top-0 left-0 w-full h-1.5 bg-gray-200 group-hover:bg-gradient-to-r group-hover:from-[#032B4E] group-hover:to-[#E06639] transition-all duration-500" />
                            <div className="absolute inset-0 bg-gradient-to-br from-[#032B4E]/[0.02] to-[#E06639]/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                            <div className="p-10 flex flex-col items-center text-center flex-grow relative z-10">
                                <div className="level-icon-bg w-28 h-28 rounded-full bg-[#f0f7ff] flex items-center justify-center transition-colors duration-500 mb-8 shadow-inner relative">
                                    <div className="level-icon relative z-10">
                                        <Package className="w-12 h-12 text-[#032B4E] transition-colors duration-500" strokeWidth={1.5} />
                                    </div>
                                </div>
                                <h3 className="text-[26px] text-[#0F1116] mb-4 group-hover:text-[#032B4E] transition-colors duration-300"
                                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700 }}>
                                    SKU Based Forecasting
                                </h3>
                                <p className="text-[15px] text-[#5E6775] leading-relaxed mb-10 flex-grow px-2"
                                    style={{ fontFamily: "Poppins, sans-serif" }}>
                                    Generate detailed, granular predictions for distinct Stock Keeping Units (SKUs) and specific geographic locations.
                                </p>
                                <div className="flex items-center justify-center gap-3 bg-gray-50 text-[#032B4E] group-hover:bg-[#E06639] group-hover:text-white px-8 py-3 rounded-lg font-semibold text-[16px] transition-all duration-300 w-[80%] border-2 border-[#1F4280] group-hover:border-[#E06639]">
                                    {forecastingType === 'sku' ? 'Continue' : 'Select Option'}
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
                                </div>
                            </div>
                        </div>

                        {/* Feature Level Card */}
                        <div
                            onClick={() => handleSelectType('feature')}
                            className={`level-card group cursor-pointer relative bg-white rounded-2xl border-2 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden flex flex-col h-full anim-fade-in-1 ${forecastingType === 'feature' ? 'level-card--selected' : 'border-[#1F4280]'}`}
                        >
                            {forecastingType === 'feature' && <SelectedChip />}
                            <div className="absolute top-0 left-0 w-full h-1.5 bg-gray-200 group-hover:bg-gradient-to-r group-hover:from-[#032B4E] group-hover:to-[#E06639] transition-all duration-500" />
                            <div className="absolute inset-0 bg-gradient-to-br from-[#032B4E]/[0.02] to-[#E06639]/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                            <div className="p-10 flex flex-col items-center text-center flex-grow relative z-10">
                                <div className="level-icon-bg w-28 h-28 rounded-full bg-[#f0f7ff] flex items-center justify-center transition-colors duration-500 mb-8 shadow-inner relative">
                                    <div className="level-icon relative z-10">
                                        <BarChart3 className="w-12 h-12 text-[#032B4E] transition-colors duration-500" strokeWidth={1.5} />
                                    </div>
                                </div>
                                <h3 className="text-[26px] text-[#0F1116] mb-4 group-hover:text-[#032B4E] transition-colors duration-300"
                                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700 }}>
                                    Feature Based Forecasting
                                </h3>
                                <p className="text-[15px] text-[#5E6775] leading-relaxed mb-10 flex-grow px-2"
                                    style={{ fontFamily: "Poppins, sans-serif" }}>
                                    Build forecasts based on product attributes, characteristics, and market features rather than specific items.
                                </p>
                                <div className="flex items-center justify-center gap-3 bg-gray-50 text-[#032B4E] group-hover:bg-[#E06639] group-hover:text-white px-8 py-3 rounded-lg font-semibold text-[16px] transition-all duration-300 w-[80%] border-2 border-[#1F4280] group-hover:border-[#E06639]">
                                    {forecastingType === 'feature' ? 'Continue' : 'Select Option'}
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
                                </div>
                            </div>
                        </div>

                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 30 }}>
                        <button
                            onClick={() => setActiveTab('Stage2')}
                            className="px-8 py-2.5 bg-[#E06639] hover:bg-[#c4552a] text-white rounded-lg transition-all shadow-lg hover:shadow-xl"
                            style={{ fontFamily: "Poppins, sans-serif", fontSize: 14, fontWeight: 600 }}
                        >
                            ← Back
                        </button>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
}

// "Selected" pill rendered inside the chosen card
function SelectedChip() {
    return (
        <div style={{
            position: 'absolute', top: 14, right: 14, zIndex: 20,
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: '#E06639', color: '#fff',
            borderRadius: 20, padding: '4px 12px',
            fontFamily: "Poppins, sans-serif", fontSize: 12, fontWeight: 700,
            boxShadow: '0 2px 8px rgba(184,132,47,0.35)',
        }}>
            <CheckCircle2 size={13} /> Selected
        </div>
    );
}

// Badge displayed at top of the SKU flow reminding user of their choice
function SelectionBadge({ type, onChangeSelection }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 16, flexWrap: 'wrap',
            background: 'linear-gradient(135deg, #fff8ec, #fffdf6)',
            border: '1.5px solid #e8c87a',
            borderLeft: '5px solid #E06639',
            borderRadius: 10,
            padding: '12px 18px',
            marginBottom: 24,
            boxShadow: '0 4px 12px rgba(184,132,47,0.1)',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckCircle2 size={18} color="#E06639" />
                <span style={{ fontFamily: "Poppins, sans-serif", fontSize: 13, color: '#263243' }}>
                    Selected:&nbsp;<strong style={{ color: '#032B4E' }}>{LABEL[type]}</strong>
                </span>
            </div>
            <button
                onClick={onChangeSelection}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: '#fff', color: '#5E6775',
                    border: '1.5px solid #d1d5db', borderRadius: 8,
                    padding: '6px 14px',
                    fontFamily: "Poppins, sans-serif", fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                }}
            >
                <RefreshCw size={13} /> Change Selection
            </button>
        </div>
    );
}
